import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Modal with fixed header, scrollable body, and fixed footer actions.
 * Portaled to document.body so it stays above filter bars and tables.
 * Theme: PaceHRM DS surface / border / fg tokens.
 */
export default function ModalShell({
  title,
  subtitle,
  onClose,
  children,
  footer,
  headerActions,
  maxWidth = 'max-w-3xl',
  zIndex = 'z-[12000]',
  corporate = false,
  contentClassName = '',
  /** Rounded inputs, buttons, and info panels inside the modal (works outside .portal-main). */
  curvy = false,
  candidateCurvy,
  /** When false, clicking the backdrop does not close the modal (use for forms with unsaved input). */
  closeOnBackdropClick = true
}) {
  const [bodyCandidateLayout, setBodyCandidateLayout] = useState(false);
  useEffect(() => {
    const sync = () =>
      setBodyCandidateLayout(document.body.classList.contains('portal-layout-candidate'));
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const curvyModal = curvy ?? candidateCurvy ?? bodyCandidateLayout;
  const panelClass = curvyModal ? 'rounded-2xl overflow-hidden portal-curvy-modal' : 'rounded-2xl overflow-hidden';
  const bodyWrap = corporate ? 'candidate-form corporate-modal' : curvyModal ? 'portal-curvy-modal-body' : '';

  const modal = (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center ${zIndex} p-4`}
      onClick={closeOnBackdropClick ? onClose : undefined}
    >
      <div
        className={`hiring-theme bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-panel)] w-full ${maxWidth} max-h-[90vh] flex flex-col border border-[hsl(var(--ds-border))] ${panelClass}`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div
          className={`flex items-start justify-between border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] flex-shrink-0 ${
            corporate ? 'px-5 py-3' : 'px-6 py-4'
          }`}
        >
          <div className="min-w-0 flex-1 pr-3">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={`font-semibold text-[hsl(var(--ds-fg))] ${corporate ? 'text-base' : 'text-lg'}`}>{title}</h2>
              {headerActions}
            </div>
            {subtitle && (
              <p className={`text-[hsl(var(--ds-muted-fg))] mt-0.5 ${corporate ? 'text-xs' : 'text-sm'}`}>{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            className="rounded-lg p-1 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))] text-xl leading-none"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div
          className={`overflow-y-auto flex-1 min-h-0 bg-[hsl(var(--ds-surface))] ${bodyWrap} ${contentClassName} ${
            corporate ? 'px-5 py-3' : 'px-6 py-4'
          }`.trim()}
        >
          {children}
        </div>

        {footer && (
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] flex-shrink-0 rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
